import { useMemo } from 'react'
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
  useMaterialReactTable,
} from 'material-react-table'
import { Box, IconButton, Tooltip } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import BlockIcon from '@mui/icons-material/Block'
import DeleteIcon from '@mui/icons-material/Delete'
import type { IReviewReportWithDetails } from '../../../api/reviews'
import { DateCell } from '../components/TableCells'

interface ReportReviewsTableProps {
  data: IReviewReportWithDetails[]
  isLoading: boolean
  pagination: MRT_PaginationState
  rowCount: number
  onPaginationChange: (updater: MRT_PaginationState | ((old: MRT_PaginationState) => MRT_PaginationState)) => void
  onView: (report: IReviewReportWithDetails) => void
  onIgnore: (report: IReviewReportWithDetails) => void
  onDelete: (report: IReviewReportWithDetails) => void
}

function TextCell({ text, maxWidth = 200 }: { text: string; maxWidth?: number }) {
  return (
    <div style={{ 
      maxWidth, 
      overflow: 'hidden', 
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical'
    }}>
      {text || '-'}
    </div>
  )
}

function RatingCell({ rating }: { rating: number }) {
  return <span>{rating > 0 ? `${rating}/5` : '-'}</span>
}

export function ReportReviewsTable({
  data,
  isLoading,
  pagination,
  rowCount,
  onPaginationChange,
  onView,
  onIgnore,
  onDelete,
}: ReportReviewsTableProps) {
  const columns = useMemo<MRT_ColumnDef<IReviewReportWithDetails>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'Report ID',
        size: 120,
        Cell: ({ cell }) => <TextCell text={cell.getValue<string>()} maxWidth={120} />,
      },
      {
        accessorKey: 'reason',
        header: 'Report Reason',
        size: 200,
        Cell: ({ cell }) => <TextCell text={cell.getValue<string>()} />,
      },
      {
        accessorKey: 'reporterUsername',
        header: 'Reported By',
        size: 150,
        Cell: ({ cell }) => <TextCell text={cell.getValue<string>()} maxWidth={150} />,
      },
      {
        accessorKey: 'reviewText',
        header: 'Review Content',
        size: 250,
        Cell: ({ cell }) => <TextCell text={cell.getValue<string>()} maxWidth={250} />,
      },
      {
        accessorKey: 'reviewRating',
        header: 'Review Rating',
        size: 100,
        Cell: ({ cell }) => <RatingCell rating={cell.getValue<number>()} />,
      },
      {
        accessorKey: 'reviewAuthorUsername',
        header: 'Review Author',
        size: 150,
        Cell: ({ cell }) => <TextCell text={cell.getValue<string>()} maxWidth={150} />,
      },
      {
        accessorKey: 'fragranceName',
        header: 'Fragrance',
        size: 150,
        Cell: ({ cell }) => <TextCell text={cell.getValue<string>()} maxWidth={150} />,
      },
      {
        accessorKey: 'created',
        header: 'Reported At',
        size: 150,
        Cell: ({ cell }) => <DateCell date={cell.getValue<string>()} />,
      },
    ],
    [],
  )

  const table = useMaterialReactTable({
    columns,
    data,
    enableEditing: false,
    enableRowActions: true,
    positionActionsColumn: 'first',
    getRowId: (row) => row.id,
    manualPagination: true,
    rowCount,
    state: {
      isLoading,
      pagination,
      columnPinning: {
        left: ['mrt-row-actions'],
      },
    },
    onPaginationChange,
    enableColumnFilters: false,
    enableColumnActions: false,
    enableSorting: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableGlobalFilter: false,
    enableColumnPinning: true,
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '0.5rem' }}>
        <Tooltip title="View Review">
          <IconButton onClick={() => onView(row.original)} color="primary">
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ignore Report">
          <IconButton onClick={() => onIgnore(row.original)} color="warning">
            <BlockIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete Review">
          <IconButton onClick={() => onDelete(row.original)} color="error">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    muiTableContainerProps: {
      sx: {
        maxHeight: '100%',
        height: '100%',
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: '1px solid #e0e0e0',
      },
    },
  })

  return <MaterialReactTable table={table} />
}
